import { getApiBaseUrl } from '../axios/Utils/constants';
import { TrackingRealtimeLog } from '../types/Tracking/types';

type SocketIoLike = {
  connected: boolean;
  on(event: string, callback: (...args: any[]) => void): SocketIoLike;
  emit(event: string, payload?: unknown): SocketIoLike;
  disconnect(): SocketIoLike;
};

const createSocketIoClient = (): ((url: string, options: Record<string, unknown>) => SocketIoLike) =>
  require('socket.io-client');

export type TrackingRealtimeStatus = 'idle' | 'connecting' | 'connected' | 'error' | 'disconnected';

type TrackingRealtimeListener = (payload: TrackingRealtimeLog) => void;
type TrackingRealtimeStatusListener = (status: TrackingRealtimeStatus) => void;

const LOG_EVENT = 'tracking:log';
const STREAM_EVENT = 'tracking:stream';

let socket: SocketIoLike | null = null;
let status: TrackingRealtimeStatus = 'idle';
const logListeners = new Set<TrackingRealtimeListener>();
const statusListeners = new Set<TrackingRealtimeStatusListener>();

const notifyStatus = (nextStatus: TrackingRealtimeStatus) => {
  status = nextStatus;
  statusListeners.forEach((listener) => {
    try {
      listener(nextStatus);
    } catch (error) {
      console.error('[TrackingRealtime] Error in status listener', error);
    }
  });
};

export const getTrackingRealtimeStatus = (): TrackingRealtimeStatus => status;

export const subscribeTrackingRealtimeStatus = (
  listener: TrackingRealtimeStatusListener,
): (() => void) => {
  statusListeners.add(listener);
  listener(status);
  return () => {
    statusListeners.delete(listener);
  };
};

export const subscribeTrackingRealtimeLogs = (listener: TrackingRealtimeListener): (() => void) => {
  logListeners.add(listener);
  return () => {
    logListeners.delete(listener);
  };
};

const emitLog = (payload: TrackingRealtimeLog) => {
  logListeners.forEach((listener) => {
    try {
      listener(payload);
    } catch (error) {
      console.error('[TrackingRealtime] Error in log listener', error);
    }
  });
};

export const connectTrackingRealtime = (token: string): void => {
  if (!token) return;
  if (socket?.connected) return;

  const baseUrl = getApiBaseUrl().replace(/\/$/, '');
  const io = createSocketIoClient();
  notifyStatus('connecting');

  socket = io(baseUrl, {
    path: '/socket.io',
    transports: ['polling', 'websocket'],
    upgrade: true,
    forceNew: true,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1500,
    timeout: 10000,
    query: { token },
  });

  socket.on('connect', () => {
    notifyStatus('connected');
  });

  socket.on('disconnect', () => {
    notifyStatus('disconnected');
  });

  socket.on('connect_error', () => {
    notifyStatus('error');
  });

  socket.on(STREAM_EVENT, (payload: TrackingRealtimeLog | TrackingRealtimeLog[]) => {
    if (Array.isArray(payload)) {
      payload.forEach(emitLog);
      return;
    }
    emitLog(payload);
  });
};

export const disconnectTrackingRealtime = (): void => {
  socket?.disconnect();
  socket = null;
  notifyStatus('disconnected');
};

export const emitTrackingRealtimeLog = (payload: TrackingRealtimeLog): void => {
  if (!socket || !socket.connected) return;
  socket.emit(LOG_EVENT, payload);
};
